// @skade/contracts — operative ESO-sjekklister (Enhetens StyrkeOppbygging).
//
// Dette er de EKTE, operative sjekklistene fra GNÅ-appen (lib/screens/eso/
// eso_models.dart) — SAMME mal-id-er og SAMME punkttekster, ingen divergens —
// utvidet OPPOVER i kommandokjeden (kompani/bataljon/brigade) slik at befal kan
// DISTRIBUERE en sjekkliste ned til et echelon + territorium, og se KLAR-status
// RULLE OPP Lag → Tropp → Kompani → Bataljon → Brigade.
//
// Malen er malbasert: ROLLE (standard/sanitet/skarpskytter) × ECHELON. Rolle-
// spesifikke punkter legges FORAN nivåets basispunkter (en sanitetssoldat får
// både sanitets- og basispunkter) — nøyaktig som GNÅ. Ren, testbar logikk.
import { z } from 'zod';
import { Echelon, echelonRank, ECHELON_LABEL, toEchelon, } from './hierarchy.js';
import { UnitAddressSchema } from './hierarchy.js';
// ── Rolle ────────────────────────────────────────────────────────────────────
export const EsoRole = z.enum(['standard', 'sanitet', 'skarpskytter']);
export const ESO_ROLE_LABEL = {
    standard: 'Standard', sanitet: 'Sanitet', skarpskytter: 'Skarpskytter',
};
export function toEsoRole(v) {
    const r = EsoRole.safeParse(v);
    return r.success ? r.data : 'standard';
}
/** Stabil, versjonert mal-id (samme format som GNÅ: «rolle.nivå.v1»). */
export function templateId(role, level) {
    return `${role}.${level}.v1`;
}
/** Basispunkter per echelon. enkeltmann..tropp = IDENTISK med GNÅ; kompani..
 * brigade utvider oppover (stab/samband/ildstøtte/logistikk/etterretning). */
const BASE_BY_LEVEL = {
    enkeltmann: [
        { id: 'e_vpn', text: 'Personlig våpen kontrollert og funksjonstestet' },
        { id: 'e_amm', text: 'Ammunisjon fylt opp iht. utrustningsliste' },
        { id: 'e_samb', text: 'Samband testet, batterier ladet/reserve' },
        { id: 'e_forp', text: 'Forpleining og vann for 24t' },
        { id: 'e_forst', text: 'Personlig førstehjelp (IFAK) komplett' },
        { id: 'e_verne', text: 'Verneutstyr og bekledning tilpasset vær' },
        { id: 'e_ordre', text: 'Forstått ordre, samband- og signalplan' },
    ],
    makkerpar: [
        { id: 'm_sektor', text: 'Sektorer og observasjonsansvar fordelt' },
        { id: 'm_samb', text: 'Internkommunikasjon (stemme/signal) avtalt' },
        { id: 'm_kryss', text: 'Kryssjekk av hverandres utrustning gjennomført' },
        { id: 'm_medevac', text: 'Makker kjenner egen blodtype/allergi/MEDEVAC' },
    ],
    lag: [
        { id: 'l_appell', text: 'Mannskapsappell — alle gjort rede for' },
        { id: 'l_vpn', text: 'Lagets tyngre våpen og optikk klargjort' },
        { id: 'l_samb', text: 'Lagssamband oppe mot tropp' },
        { id: 'l_forsyn', text: 'Forsyning og ammunisjonsfordeling verifisert' },
        { id: 'l_ordre', text: 'Lagsordre gitt og forstått' },
    ],
    tropp: [
        { id: 't_lag', text: 'Alle lag meldt klar' },
        { id: 't_samb', text: 'Troppsnett og reservesamband testet' },
        { id: 't_sanit', text: 'Sanitets- og evakueringsplan bekreftet' },
        { id: 't_logi', text: 'Logistikk og etterforsyning på plass' },
        { id: 't_ordre', text: 'Troppsordre kvittert av lagførere' },
    ],
    // ── Utvidelse oppover (operative stabssjekklister) ──
    kompani: [
        { id: 'k_tropp', text: 'Alle tropper meldt klar' },
        { id: 'k_ko', text: 'Kommandoplass (KO) etablert og bemannet' },
        { id: 'k_samb', text: 'Kompaninett + reserve testet mot bataljon' },
        { id: 'k_ildstotte', text: 'Ildstøtte- og sanitetsplan koordinert' },
        { id: 'k_logi', text: 'Etterforsyning (klasse I/III/V) fremskutt' },
        { id: 'k_ordre', text: 'Kompaniordre gitt, kvittert av troppssjefer' },
    ],
    bataljon: [
        { id: 'b_kompani', text: 'Alle kompanier meldt klar' },
        { id: 'b_ko', text: 'Bataljonens KO og reserve-KO oppe' },
        { id: 'b_samb', text: 'Bataljonsnett + samband mot brigade/distrikt verifisert' },
        { id: 'b_etterr', text: 'Etterretningsbilde (PIR/CCIR) oppdatert og delt' },
        { id: 'b_ildstotte', text: 'Ildstøtteplan og prioriteter fastsatt' },
        { id: 'b_logi', text: 'Forsynings- og evakueringskjede (role 1/2) bekreftet' },
        { id: 'b_ordre', text: 'Bataljonsordre utgitt og kvittert' },
    ],
    brigade: [
        { id: 'br_bataljon', text: 'Alle bataljoner/avdelinger meldt klar' },
        { id: 'br_ko', text: 'Brigadens KO og stabsfunksjoner (G1–G6) bemannet' },
        { id: 'br_samb', text: 'Sambands- og K2IS-plan oppe og sikret' },
        { id: 'br_etterr', text: 'Etterretning og målbilde (ISR) synkronisert' },
        { id: 'br_ildstotte', text: 'Ildstøtte- og luftromskoordinering fastsatt' },
        { id: 'br_logi', text: 'Logistikk- og sanitetsoppbygging (role 2/3) bekreftet' },
        { id: 'br_samvirke', text: 'Samvirke med nabo/allierte og sivile myndigheter avklart' },
        { id: 'br_ordre', text: 'Brigadeordre utgitt, kvittert av avdelingssjefer' },
    ],
};
/** Rolle-spesifikke punkter (legges FORAN basispunktene) — som GNÅ. */
const BY_ROLE = {
    sanitet: [
        { id: 's_sekk', text: 'Sanitetssekk fylt og forseglet' },
        { id: 's_medvac', text: 'MEDEVAC-9-linje og samband klart' },
        { id: 's_triage', text: 'Triage-materiell og merkelapper klart' },
        { id: 's_medikament', text: 'Medikamenter innen holdbarhet kontrollert' },
    ],
    skarpskytter: [
        { id: 'sk_optikk', text: 'Optikk nullstilt og kontrollert' },
        { id: 'sk_ballistikk', text: 'Ballistikkdata/DOPE oppdatert for forhold' },
        { id: 'sk_kamu', text: 'Kamuflasje (ghillie) og observasjonsutstyr klart' },
        { id: 'sk_avstand', text: 'Avstandsmåler og vindmåler funksjonstestet' },
    ],
    standard: [],
};
/** Bygg malen for en rolle+echelon-kombinasjon (rolle-punkter foran base). */
export function templateFor(role, level) {
    return { role, level, items: [...BY_ROLE[role], ...BASE_BY_LEVEL[level]] };
}
/** Slå opp en mal fra dens id («rolle.nivå.v1»). Ukjent → standard.enkeltmann. */
export function templateById(id) {
    const [role, level] = id.split('.');
    return templateFor(toEsoRole(role), toEchelon(level));
}
// ── Distribusjon (befal → echelon + territorium) ────────────────────────────
/** En sjekkliste-tildeling: befal DISTRIBUERER en mal ned til et mål (echelon +
 * territorium). Wire-format `skade:checklist.assign.v1` (kan gå over hub-collab
 * og — når to-veis-broen finnes — videre til GNÅ). */
export const ChecklistAssignmentSchema = z.object({
    id: z.string().max(64),
    role: EsoRole.default('standard'),
    level: Echelon.default('lag'),
    /** Målenhet (hvem skal utføre): echelon + HV-territorium + kommandosti. */
    target: UnitAddressSchema,
    issuedBy: z.string().max(64),
    issuedMs: z.number(),
    dueMs: z.number().optional(),
    note: z.string().max(2000).default(''),
});
/** Tildelingens mal-id (utledet — alltid konsistent med role+level). */
export function assignmentTemplateId(a) {
    return templateId(a.role, a.level);
}
const ASSIGN_WIRE = 'checklist.assign.v1';
/** ChecklistAssignment → GNÅ/hub-wire («skade»-diskriminant). */
export function assignmentToWire(a) {
    return {
        skade: ASSIGN_WIRE,
        id: a.id, role: a.role, level: a.level, template: assignmentTemplateId(a),
        target: a.target, by: a.issuedBy, issuedMs: a.issuedMs,
        dueMs: a.dueMs ?? null, note: a.note,
    };
}
/** Wire → ChecklistAssignment (defensiv, null ved ugyldig). `by`/senderId
 * autentiserer utsteder (aldri stol på et fritt «by»-felt over `senderId`). */
export function parseAssignment(body, senderId) {
    const t = body.trim();
    if (!t.startsWith('{'))
        return null;
    let m;
    try {
        m = JSON.parse(t);
    }
    catch {
        return null;
    }
    if (!m || typeof m !== 'object' || m['skade'] !== ASSIGN_WIRE)
        return null;
    const o = m;
    const parsed = ChecklistAssignmentSchema.safeParse({
        id: o['id'],
        role: o['role'],
        level: o['level'],
        target: o['target'] ?? {},
        issuedBy: senderId ?? o['by'],
        issuedMs: o['issuedMs'],
        dueMs: typeof o['dueMs'] === 'number' ? o['dueMs'] : undefined,
        note: o['note'],
    });
    return parsed.success ? parsed.data : null;
}
// ── Enkeltmannsstatus (kanonisk, rikere enn gna-bridge sin lette variant) ────
/** Én enkeltmanns siste innmeldte ESO-status (speiler GNÅ eso.status.v1). */
export const EsoSoldierStatusSchema = z.object({
    soldierId: z.string().max(120),
    name: z.string().max(200).default(''),
    /** Selvrapportert enhet (f.eks. «LAG-1») — knytter status til ORBAT-treet. */
    unit: z.string().max(120).default('—'),
    role: EsoRole.default('standard'),
    level: Echelon.default('enkeltmann'),
    templateId: z.string().max(64).default(''),
    checked: z.array(z.string().max(64)).default([]),
    total: z.number().int().min(0).max(1000).default(0),
    ready: z.boolean().default(false),
    tsMs: z.number().default(0),
});
export const ESO_READINESS_LABEL = {
    notStarted: 'Ikke startet', partial: 'Delvis', ready: 'Klar til strid',
};
export const ESO_READINESS_COLOR = {
    notStarted: '#8a8f98', partial: '#ffd24a', ready: '#4bd15b',
};
/** Enkeltmannens beredskap (som GNÅ: KLAR-flagg > noe avhuket > ikke startet). */
export function soldierReadiness(s) {
    if (s.ready)
        return 'ready';
    if (s.checked.length > 0)
        return 'partial';
    return 'notStarted';
}
/** Parse GNÅ eso.status.v1 → kanonisk status. senderId binder identitet
 * (anti-spoof): statusen tilskrives den AUTENTISERTE avsenderen. */
export function parseEsoSoldierStatus(body, senderId) {
    const t = body.trim();
    if (!t.startsWith('{'))
        return null;
    let m;
    try {
        m = JSON.parse(t);
    }
    catch {
        return null;
    }
    if (!m || typeof m !== 'object')
        return null;
    const o = m;
    if (o['gna'] !== 'eso.status.v1')
        return null;
    const soldier = senderId ?? (typeof o['soldier'] === 'string' ? o['soldier'] : undefined);
    if (!soldier)
        return null;
    const checked = Array.isArray(o['checked']) ? o['checked'].filter((c) => typeof c === 'string') : [];
    const parsed = EsoSoldierStatusSchema.safeParse({
        soldierId: soldier,
        name: typeof o['name'] === 'string' ? o['name'] : soldier,
        unit: typeof o['squad'] === 'string' ? o['squad'] : (typeof o['unit'] === 'string' ? o['unit'] : '—'),
        role: o['role'],
        level: o['level'],
        templateId: typeof o['template'] === 'string' ? o['template'] : '',
        checked,
        total: typeof o['total'] === 'number' ? o['total'] : 0,
        ready: o['ready'] === true,
        tsMs: typeof o['tsMs'] === 'number' ? o['tsMs'] : 0,
    });
    return parsed.success ? parsed.data : null;
}
/** Behold nyeste status per enkeltmann (høyeste tsMs vinner). */
export function latestPerSoldier(statuses) {
    const latest = new Map();
    for (const s of statuses) {
        const cur = latest.get(s.soldierId);
        if (!cur || s.tsMs >= cur.tsMs)
            latest.set(s.soldierId, s);
    }
    return [...latest.values()];
}
/** Aggreger en samling beredskaps-verdier (som GNÅ: alle klar → klar; alle
 * ikke-startet → ikke startet; ellers delvis; tom → ikke startet). */
export function aggregateReadiness(items) {
    if (items.length === 0)
        return 'notStarted';
    if (items.every((r) => r === 'ready'))
        return 'ready';
    if (items.every((r) => r === 'notStarted'))
        return 'notStarted';
    return 'partial';
}
/**
 * Dekorér et ORBAT-tre med rullet ESO-beredskap. `soldiersOf` gir enkeltmenn
 * rapportert direkte på en enhet (matchet på selvrapportert `unit` → enhets-id
 * eller -navn av kalleren). Ruller KLAR-status oppover kommandokjeden —
 * kjernen i «se hele brigaden bygge stridsevne fra bunnen».
 */
export function rollupEso(node, soldiersOf) {
    const soldiers = latestPerSoldier(soldiersOf(node.value));
    const children = node.children.map((c) => rollupEso(c, soldiersOf));
    const own = aggregateReadiness(soldiers.map(soldierReadiness));
    const contributors = [
        ...children.map((c) => c.rolled),
        ...(soldiers.length > 0 ? [own] : []),
    ];
    const rolled = aggregateReadiness(contributors);
    const ready = soldiers.filter((s) => s.ready).length + children.reduce((a, c) => a + c.ready, 0);
    const total = soldiers.length + children.reduce((a, c) => a + c.total, 0);
    return { unit: node.value, own, rolled, ready, total, soldiers, children };
}
/** Hjelper: én enhets samlede beredskap gitt en flat liste enkeltmenn på den
 * (uten tre). Nyttig for ett enkelt lag i UI. */
export function unitReadiness(statuses) {
    return aggregateReadiness(latestPerSoldier(statuses).map(soldierReadiness));
}
/** Sorter echelon-nivåer laveste→høyeste (til distribusjonsvelgeren). */
export function distributableLevels() {
    return [...Echelon.options].sort((a, b) => echelonRank(a) - echelonRank(b));
}
/** Kort etikett for en tildeling: «Sanitet · Lag». */
export function assignmentLabel(a) {
    return `${ESO_ROLE_LABEL[a.role]} · ${ECHELON_LABEL[a.level]}`;
}
//# sourceMappingURL=eso-checklist.js.map